import clsx from "clsx";
import React, { useState } from "react";
import { HStack } from "web/react/emo/h-stack";
import { Heading } from "web/react/emo/heading";
import { ListItem } from "web/react/emo/list-item/list-item";
import { Text } from "web/react/emo/text";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { gettext } from "web/script/modules/django-i18n";
import { useAutoCompleteContext } from "./auto-complete.context";
import * as styles from "./auto-complete.css";

export function AutoComplete(): React.ReactElement {
    const {
        activeSuggestionIndex,
        querySuggestions,
        profileSuggestions,
        makeSuggestionActive,
        onSuggestionClick,
        isVisible,
    } = useAutoCompleteContext();

    const [touchMoved, setTouchMoved] = useState<boolean>(false);
    const { isDesktopViewport } = useDomViewport();

    const profileSuggestionsLimit: number = isDesktopViewport ? 6 : 3;
    const querySuggestionsLimit: number = isDesktopViewport ? 9 : 3;
    /**
     * Used by touch device
     */
    function onTouchMove(): void {
        setTouchMoved(true);
    }

    /**
     * Used by touch device
     */
    function onTouchStart(): void {
        setTouchMoved(false);
    }

    /**
     * Used by touch device, calls onSuggestionClick only when it's not scrolling
     */
    function onTouchEnd(event): void {
        if (!touchMoved) {
            onSuggestionClick(event);
        }
    }

    function onMouseEnter(event): void {
        makeSuggestionActive(parseInt(event.target.dataset.suggestionIndex, 0));
    }

    function onMouseLeave(event): void {
        makeSuggestionActive(parseInt(event.target.dataset.suggestionIndex, 0));
    }

    return (
        <View
            className={clsx(styles.suggestionsContainer, {
                hidden: !isVisible,
            })}
        >
            <View
                paddingTop={{ lg: "none", sm: "xxs" }}
                paddingBottom={{ lg: "sm", sm: "xxs" }}
                className={styles.headingWrapper}
            >
                {profileSuggestions.length > 0 && (
                    <Heading textStyle={"subhead"} className={styles.heading}>
                        {
                            // "Top Result"
                            gettext("search.suggestions.top_result.label")
                        }
                    </Heading>
                )}
            </View>

            <View className={styles.suggestionList} paddingBottom={{ lg: "md", sm: "xxs" }}>
                <HStack wrap>
                    {profileSuggestions.map(
                        (suggestion, index) =>
                            profileSuggestionsLimit - 1 !== index && (
                                <div
                                    key={`profile_${index}_${suggestion.profile_name}`}
                                    className={clsx(styles.suggestion)}
                                >
                                    <ListItem
                                        className={clsx(
                                            styles.listItem,
                                            activeSuggestionIndex === index &&
                                                styles.activeSuggestion
                                        )}
                                        data-suggestion-type="profile"
                                        data-suggestion-index={index}
                                        onTouchStart={onTouchStart}
                                        onTouchMove={onTouchMove}
                                        onTouchEnd={onTouchEnd}
                                        onMouseEnter={onMouseEnter}
                                        onMouseLeave={onMouseLeave}
                                        onMouseDown={onSuggestionClick}
                                        showChevron
                                    >
                                        <div className={styles.imageWrapper}>
                                            <img
                                                className={styles.image}
                                                src={suggestion.profile_avatar}
                                                alt={suggestion.profile_name}
                                            />
                                        </div>
                                        <div className={styles.profileInfo}>
                                            <Text textStyle={"body-3"}>
                                                {suggestion.profile_name}
                                            </Text>
                                            <Text
                                                textStyle={"footnote"}
                                                className={styles.footnote}
                                            >
                                                {suggestion.profile_type}
                                            </Text>
                                        </div>
                                    </ListItem>
                                    <div className={styles.divider} />
                                </div>
                            )
                    )}
                </HStack>
            </View>
            <View
                paddingTop={{ lg: "none", sm: "sm" }}
                paddingBottom={{ lg: "sm", sm: "xxs" }}
                className={styles.headingWrapper}
            >
                {querySuggestions.length > 0 && (
                    <Heading textStyle={"subhead"} className={styles.heading}>
                        {
                            // "Other Suggestions"
                            gettext("search.suggestions.other_suggestions.label")
                        }
                    </Heading>
                )}
            </View>
            <View className={styles.suggestionList} paddingBottom={{ lg: "none", sm: "md" }}>
                <HStack wrap>
                    {querySuggestions.map(
                        (suggestion, index) =>
                            querySuggestionsLimit - 1 !== index && (
                                <div
                                    key={`query_${index}_${suggestion.query}`}
                                    className={clsx(styles.suggestion)}
                                >
                                    <ListItem
                                        size="xs"
                                        className={clsx(
                                            styles.listItem,
                                            activeSuggestionIndex ===
                                                profileSuggestions.length + index &&
                                                styles.activeSuggestion
                                        )}
                                        data-suggestion-type="query"
                                        data-suggestion-index={profileSuggestions.length + index}
                                        onTouchStart={onTouchStart}
                                        onTouchMove={onTouchMove}
                                        onTouchEnd={onTouchEnd}
                                        onMouseEnter={onMouseEnter}
                                        onMouseLeave={onMouseLeave}
                                        onMouseDown={onSuggestionClick}
                                        showChevron
                                    >
                                        {suggestion.query}
                                    </ListItem>
                                    <div className={styles.divider} />
                                </div>
                            )
                    )}
                </HStack>
            </View>
        </View>
    );
}
